import {
    Divider,
    Grid,
    IconButton,
    Paper,
    styled,
    SxProps,
    Theme,
    Tooltip,
    Collapse,
    Typography,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { PageTitle } from "./PageTitle";
import React, { useState } from "react";
import { ChevronLeft } from "@mui/icons-material";

const SectionTitle = styled(PageTitle)(() => ({
    fontSize: "16px",
    lineHeight: "24px",
}));

type TitledSectionProps = React.PropsWithChildren<{
    id?: string;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    variant?: "default" | "bordered";
    sx?: SxProps<Theme>;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    infoText?: string;
    divider?: boolean;
    maxContentHeight?: number | string;
}>;

export function TitledSection(props: TitledSectionProps) {
    const hasChildren = !!props.children;
    const [expanded, setExpanded] = useState(!props.defaultCollapsed);
    const divider = props.divider ?? true;

    return (
        <Paper
            id={props.id}
            sx={[
                (theme) => ({
                    borderRadius: theme.shape.borderRadius / 2,
                    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.07)",
                    color: theme.palette.gray[700],
                    border:
                        props.variant === "bordered"
                            ? `1px solid ${theme.palette.lightgrayBorder.main}`
                            : "none",
                }),
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            <Grid
                container
                paddingInline={2}
                paddingTop={props.collapsible ? 1 : 2}
                paddingBottom={1}
                justifyContent={props.collapsible ? "space-between" : undefined}
                alignItems="center"
                flexWrap="nowrap"
            >
                <Grid
                    container
                    item
                    direction="column"
                    minWidth={0}
                    flexWrap="nowrap"
                >
                    <SectionTitle
                        // @ts-expect-error `styled` does not pass along the `component` prop however it is valid on the root `Typography` component
                        component="span"
                        width="100%"
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            columnGap: 1,
                        }}
                    >
                        {props.title}
                        {props.infoText && (
                            <Tooltip title={props.infoText} arrow>
                                <HelpIcon color="primary" fontSize="small" />
                            </Tooltip>
                        )}
                    </SectionTitle>
                    <Typography color="inherit" sx={{ wordWrap: "break-word" }}>
                        {props.subtitle}
                    </Typography>
                </Grid>
                {props.collapsible && hasChildren && (
                    <Grid item>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded((prev) => !prev)}
                        >
                            <ChevronLeft
                                sx={{ rotate: expanded ? "-90deg" : "0deg" }}
                            />
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {divider && <Divider />}
                    <Grid
                        container
                        paddingInline={2}
                        paddingBlock={2}
                        sx={
                            props.maxContentHeight
                                ? {
                                      maxHeight: props.maxContentHeight,
                                      overflowY: "auto",
                                  }
                                : undefined
                        }
                    >
                        {props.children}
                    </Grid>
                </Collapse>
            )}
        </Paper>
    );
}
